import { useEffect } from "react";
import type { ReviewContent } from "@/pages/home/lib/reviewContent";
import {
  REVIEW_SCOPE_CLASS,
  scopeReviewCss,
} from "@/pages/home/lib/reviewContent";
import { walletLabel, type WalletState } from "@/pages/home/lib/wallet";

interface ReviewPanelProps {
  content: ReviewContent | null;
  loading: boolean;
  error: string | null;
  wallet: WalletState;
  onClose: () => void;
}

const WALLET_TONE: Record<string, string> = {
  pending: "bg-secondary-100 text-foreground-700",
  connected: "bg-accent-100 text-accent-900",
  unavailable: "bg-background-200 text-foreground-600",
  rejected: "bg-background-200 text-foreground-600",
  error: "bg-background-200 text-foreground-700",
  idle: "bg-background-200 text-foreground-600",
};

/**
 * Panoul "Review" - se deschide peste ecran, în aceeași pagină.
 * Codul utilizatorului rulează în pagina principală (nu într-un iframe),
 * deci are acces la `window.ethereum` și portofelul se deschide corect.
 * Stilurile lui sunt contractate pe container, ca să nu atingă aplicația.
 */
export default function ReviewPanel({
  content,
  loading,
  error,
  wallet,
  onClose,
}: ReviewPanelProps) {
  const label = walletLabel(wallet);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // Stilurile utilizatorului, izolate pe container.
  useEffect(() => {
    if (!content) return;

    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-review", "style");
    styleEl.textContent = scopeReviewCss(content.css, `.${REVIEW_SCOPE_CLASS}`);
    document.head.appendChild(styleEl);

    const linkEls = content.stylesheets.map((href) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.setAttribute("data-review", "stylesheet");
      document.head.appendChild(link);
      return link;
    });

    return () => {
      styleEl.remove();
      linkEls.forEach((link) => link.remove());
    };
  }, [content]);

  // Scripturile rulează în documentul principal.
  useEffect(() => {
    if (!content) return;

    let cancelled = false;
    const nodes: HTMLScriptElement[] = [];

    const loadExternal = (src: string) =>
      new Promise<void>((resolve) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = false;
        script.onload = () => resolve();
        script.onerror = () => resolve();
        document.body.appendChild(script);
        nodes.push(script);
      });

    const run = async () => {
      for (const src of content.externalScripts) {
        if (cancelled) return;
        await loadExternal(src);
      }
      for (const code of content.inlineScripts) {
        if (cancelled) return;
        const script = document.createElement("script");
        script.setAttribute("data-review", "script");
        script.textContent = code;
        document.body.appendChild(script);
        nodes.push(script);
      }
    };

    void run();

    return () => {
      cancelled = true;
      nodes.forEach((node) => node.remove());
    };
  }, [content]);

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-background-50 animate-screen-in">
      <div className="flex items-center gap-2 px-4 h-12 shrink-0 border-b border-background-200 bg-background-50">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-9 h-9 -ml-1.5 flex items-center justify-center rounded-full text-foreground-950 cursor-pointer transition-colors hover:bg-background-100 shrink-0"
        >
          <i className="ri-close-line text-xl" />
        </button>
        <p className="text-[15px] font-bold text-foreground-950 truncate">
          Review
        </p>
        {label && (
          <span
            className={`ml-auto px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${
              WALLET_TONE[wallet.status] ?? WALLET_TONE.idle
            }`}
          >
            {label}
          </span>
        )}
      </div>

      <div className="relative flex-1 min-h-0">
        {loading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <span className="w-8 h-8 rounded-full border-2 border-background-200 border-t-primary-500 animate-spin" />
            <p className="text-sm text-foreground-500">Loading…</p>
          </div>
        ) : content ? (
          <div className="absolute inset-0 overflow-auto">
            <div
              className={`${REVIEW_SCOPE_CLASS} min-h-full`}
              dangerouslySetInnerHTML={{ __html: content.html }}
            />
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center px-8">
            <div className="text-center max-w-[300px]">
              <div className="flex items-center justify-center mb-4">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-file-code-line text-2xl" />
                </span>
              </div>
              <p className="font-bold text-[15px] text-foreground-950 mb-1">
                {error ? "Could not load" : "No content yet"}
              </p>
              <p className="text-[13px] leading-relaxed text-foreground-500">
                {error ?? (
                  <>
                    Add your content in the file{" "}
                    <span className="font-semibold text-foreground-800">
                      review/index.html
                    </span>
                    , then press Review again.
                  </>
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}